/**
 * CloseoutPage — AI end-of-project closeout document (Track A, A2).
 *
 * Pick a site → "Generate closeout" → the api-shim gathers the live
 * deployed state and Claude writes the closeout document: executive
 * summary, deployed inventory, network configuration, compliance status,
 * a punch list of open items, and a sign-off readiness verdict.
 *
 * Layout mirrors /diagnose: picker bar, document on the left, history
 * rail on the right.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  Cpu,
  Download,
  FileText,
  Loader2,
  Square,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  SitePicker,
  type PickedSite,
  type SiteVendor,
} from "@/components/app/site-picker";
import { arubaSlug } from "@/lib/aruba-slug";
import { MarkdownView } from "@/components/app/markdown-view";
import { cn } from "@/lib/cn";
import { useSites } from "@/stores/sites";
import { useArubaSites } from "@/stores/aruba-sites";
import { useCliSites } from "@/stores/cli-sites";
import {
  useCloseout,
  type CloseoutDetail,
  type CloseoutDossier,
  type CloseoutReport,
  type CloseoutRun,
  type SignoffReadiness,
} from "@/stores/closeout";

/* ---------- sign-off readiness presentation ---------- */

const READINESS_META: Record<
  SignoffReadiness,
  {
    label: string;
    icon: typeof CheckCircle2;
    badge: BadgeProps["variant"];
    band: string;
    dot: string;
  }
> = {
  ready: {
    label: "Ready to sign off",
    icon: CheckCircle2,
    badge: "ok",
    band: "border-mint/25 bg-mint/10 text-mint",
    dot: "bg-mint",
  },
  conditional: {
    label: "Conditional sign-off",
    icon: AlertCircle,
    badge: "warn",
    band: "border-peach/25 bg-peach/10 text-peach",
    dot: "bg-peach",
  },
  blocked: {
    label: "Blocked — not ready",
    icon: Ban,
    badge: "err",
    band: "border-rose/25 bg-rose/10 text-rose",
    dot: "bg-rose",
  },
};

/* ---------- helpers ---------- */

function timeAgo(iso: string): string {
  const t = Date.parse(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface ShownCloseout {
  id: string;
  result: CloseoutReport;
  dossier: CloseoutDossier | null;
  model: string | null;
  durationMs: number | null;
  createdAt: string | null;
  createdBy: string | null;
}

const fromRun = (r: CloseoutRun): ShownCloseout => ({
  id: r.id,
  result: r.result,
  dossier: r.dossier,
  model: r.model,
  durationMs: r.elapsedMs,
  createdAt: null,
  createdBy: null,
});

const fromDetail = (d: CloseoutDetail): ShownCloseout => ({
  id: d.id,
  result: d.result,
  dossier: d.dossier,
  model: d.modelName,
  durationMs: d.durationMs,
  createdAt: d.createdAt,
  createdBy: d.createdBy,
});

function closeoutToMarkdown(shown: ShownCloseout, site: PickedSite): string {
  const r = shown.result;
  const stamp = shown.createdAt
    ? shown.createdAt
    : new Date().toISOString().slice(0, 16).replace("T", " ");
  const punch = r.punchList.length
    ? r.punchList.map((p) => `- [ ] ${p}`)
    : ["_No open items — site is clean._"];
  return [
    `# Project Closeout — ${site.code}`,
    "",
    `_${site.org} · generated ${stamp} UTC · ${shown.model ?? "—"}_`,
    "",
    `**Sign-off readiness: ${r.signoffReadiness.toUpperCase()}**`,
    "",
    r.signoffReason,
    "",
    "## Executive Summary",
    "",
    r.executiveSummary,
    "",
    "## Deployed Inventory",
    "",
    r.deployedInventory,
    "",
    "## Network Configuration",
    "",
    r.networkConfiguration,
    "",
    "## Compliance Status",
    "",
    r.complianceStatus,
    "",
    "## Punch List",
    "",
    ...punch,
    "",
  ].join("\n");
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- document section ---------- */

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
        {title}
      </div>
      {body.trim() ? (
        <MarkdownView text={body} compact={false} className="text-sm text-fg-dim" />
      ) : (
        <p className="m-0 text-sm italic text-fg-mute">(not provided)</p>
      )}
    </div>
  );
}

/* ---------- closeout document card ---------- */

function CloseoutCard({ shown, site }: { shown: ShownCloseout; site: PickedSite }) {
  const { result, dossier } = shown;
  const meta = READINESS_META[result.signoffReadiness];
  const Icon = meta.icon;

  function onCopy() {
    void navigator.clipboard
      .writeText(closeoutToMarkdown(shown, site))
      .then(() => toast.success("Closeout copied as markdown"))
      .catch(() => toast.error("Copy failed"));
  }

  function onDownload() {
    downloadText(`closeout-${site.slug}.md`, closeoutToMarkdown(shown, site));
    toast.success(`Downloaded closeout-${site.slug}.md`);
  }

  return (
    <Card>
      <div className={cn("flex flex-wrap items-center gap-3 border-b px-5 py-3.5", meta.band)}>
        <Icon size={18} className="shrink-0" />
        <span className="text-sm font-bold tracking-tight">{meta.label}</span>
        <span className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onCopy}>
            <Copy size={13} />
            Copy
          </Button>
          <Button size="sm" variant="ghost" onClick={onDownload}>
            <Download size={13} />
            .md
          </Button>
        </span>
      </div>

      <CardContent className="space-y-5">
        <div className="font-mono text-[10.5px] text-fg-mute">
          {shown.model ?? "—"}
          {shown.durationMs != null ? ` · ${(shown.durationMs / 1000).toFixed(1)}s` : ""}
          {" · "}
          {shown.createdAt ? timeAgo(shown.createdAt) : "just now"}
        </div>

        <p className="m-0 text-[15px] leading-relaxed text-fg">{result.executiveSummary}</p>

        <Section title="Deployed inventory" body={result.deployedInventory} />
        <Section title="Network configuration" body={result.networkConfiguration} />
        <Section title="Compliance status" body={result.complianceStatus} />

        <div>
          <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Punch list ({result.punchList.length})
          </div>
          {result.punchList.length === 0 ? (
            <p className="m-0 flex items-center gap-2 text-sm text-mint">
              <CheckCircle2 size={15} />
              No open items — site is clean.
            </p>
          ) : (
            <ul className="m-0 space-y-2 p-0">
              {result.punchList.map((item) => (
                <li key={item} className="flex gap-2.5">
                  <Square size={15} className="mt-[2px] shrink-0 text-fg-mute" />
                  <span className="text-sm leading-relaxed text-fg-dim">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={cn("rounded-xl border px-4 py-3", meta.band)}>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em]">
            Sign-off — {result.signoffReadiness}
          </div>
          <div className="text-sm leading-relaxed text-fg-dim">{result.signoffReason}</div>
        </div>

        {dossier ? (
          <div className="border-t border-line pt-4">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              Site snapshot at closeout time
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info" size="md">
                {dossier.inventory.deviceCount} devices
              </Badge>
              {dossier.configuration.vendor === "aruba" ? (
                <Badge variant="neutral" size="md">
                  {(dossier.configuration.groups ?? []).length} config groups
                </Badge>
              ) : dossier.configuration.vendor === "cli" ? (
                <Badge variant="neutral" size="md">config in golden-config</Badge>
              ) : (
                <>
                  <Badge variant="neutral" size="md">
                    {(dossier.configuration.vlans ?? []).length} VLANs
                  </Badge>
                  <Badge variant="neutral" size="md">
                    {(dossier.configuration.enabledSsids ?? []).length} SSIDs
                  </Badge>
                  <Badge variant="neutral" size="md">
                    {dossier.configuration.firewall?.l3RuleCount ?? 0} firewall rules
                  </Badge>
                </>
              )}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ---------- page ---------- */

export function CloseoutPage() {
  const [params, setParams] = useSearchParams();

  const merakiSites = useSites((s) => s.sites);
  const loadMerakiSites = useSites((s) => s.loadSites);
  const merakiStatus = useSites((s) => s.status);
  const arubaSites = useArubaSites((s) => s.data);
  const loadArubaSites = useArubaSites((s) => s.load);
  const arubaStatus = useArubaSites((s) => s.status);
  const cliSites = useCliSites((s) => s.data);
  const loadCliSites = useCliSites((s) => s.load);
  const cliStatus = useCliSites((s) => s.status);

  const generate = useCloseout((s) => s.generate);
  const loadHistory = useCloseout((s) => s.loadHistory);
  const loadDetail = useCloseout((s) => s.loadDetail);
  const generating = useCloseout((s) => s.generating);
  const loadingHistory = useCloseout((s) => s.loadingHistory);
  const historyBySlug = useCloseout((s) => s.historyBySlug);
  const errorBySlug = useCloseout((s) => s.errorBySlug);

  const [vendor, setVendor] = useState<SiteVendor>("meraki");
  const [site, setSite] = useState<PickedSite | null>(null);
  const [shown, setShown] = useState<ShownCloseout | null>(null);

  const slug = site?.slug ?? "";
  const isGenerating = !!generating[slug];
  const history = historyBySlug[slug] ?? [];
  const runError = errorBySlug[slug];

  const selectSite = useCallback(
    async (s: PickedSite) => {
      setSite(s);
      setVendor(s.vendor);
      setShown(null);
      setParams({ site: s.slug, vendor: s.vendor }, { replace: true });
      await loadHistory(s.slug);
      const hist = useCloseout.getState().historyBySlug[s.slug] ?? [];
      if (hist.length > 0) {
        const detail = await loadDetail(hist[0]!.id);
        if (detail) setShown(fromDetail(detail));
      }
    },
    [loadHistory, loadDetail, setParams],
  );

  function onVendorChange(v: SiteVendor) {
    if (v === vendor) return;
    setVendor(v);
    setSite(null);
    setShown(null);
  }

  useEffect(() => {
    const wanted = params.get("site");
    if (!wanted || site) return;
    const want = wanted.toLowerCase();
    const pv = params.get("vendor");
    const wantVendor: SiteVendor = pv === "aruba" || pv === "cli" ? pv : "meraki";
    if (wantVendor === "meraki") {
      const m = merakiSites.find((s) => s.slug === want);
      if (m) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void selectSite({ slug: m.slug, code: m.code, org: m.org, vendor: "meraki" });
      } else if (merakiStatus === "idle") {
        void loadMerakiSites();
      }
    } else if (wantVendor === "aruba") {
      const a = arubaSites.find((s) => arubaSlug(s.site_name ?? "") === want);
      if (a) {
        void selectSite({
          slug: arubaSlug(a.site_name ?? ""),
          code: a.site_name ?? "(unnamed site)",
          org: [a.city, a.country].filter(Boolean).join(", ") || "Aruba Central",
          vendor: "aruba",
        });
      } else if (arubaStatus === "idle") {
        void loadArubaSites();
      }
    } else {
      const c = cliSites.find((g) => arubaSlug(g.name) === want);
      if (c) {
        void selectSite({
          slug: arubaSlug(c.name),
          code: c.name,
          org: `${c.hostCount} host${c.hostCount === 1 ? "" : "s"} · Zabbix`,
          vendor: "cli",
        });
      } else if (cliStatus === "idle") {
        void loadCliSites();
      }
    }
  }, [params, site, merakiSites, arubaSites, cliSites, merakiStatus, arubaStatus,
      cliStatus, loadMerakiSites, loadArubaSites, loadCliSites, selectSite]);

  async function onGenerate() {
    if (!slug || isGenerating) return;
    const result = await generate(slug, site?.vendor ?? "meraki");
    if (result) {
      setShown(fromRun(result));
    } else {
      const msg = useCloseout.getState().errorBySlug[slug] || "closeout failed";
      toast.error(`Closeout failed — ${msg}`);
    }
  }

  async function onPickHistory(id: string) {
    const detail = await loadDetail(id);
    if (detail) setShown(fromDetail(detail));
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          AI
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Closeout</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 flex items-center gap-2.5 text-3xl font-extrabold tracking-tight text-aurora">
            <ClipboardCheck size={26} className="text-violet" />
            Closeout
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            AI-generated end-of-project closeout document. Opus reads the live deployed
            state of a site — device inventory, VLANs, SSIDs, firewall, compliance — and
            writes the handover document: executive summary, deployed inventory, network
            configuration, a punch list of open items, and a sign-off readiness verdict.
            Copy or download it straight into the closeout folder.
          </p>
        </div>
      </div>

      {/* Picker + generate bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="min-w-[280px] flex-1">
            <SitePicker
              value={site}
              onPick={(s) => void selectSite(s)}
              vendor={vendor}
              onVendorChange={onVendorChange}
              vendors={["meraki", "aruba", "cli"]}
              placeholder="Select a site to close out…"
            />
          </div>
          <Button onClick={() => void onGenerate()} disabled={!slug || isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <FileText size={14} />
                {shown ? "Regenerate closeout" : "Generate closeout"}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {runError && !isGenerating ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>Last closeout failed — {runError}</span>
        </output>
      ) : null}

      {/* Document + history rail */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          {isGenerating ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-20 text-center">
                <Loader2 size={28} className="animate-spin text-violet" />
                <div className="text-sm font-semibold text-fg">
                  Gathering deployed state and writing the closeout…
                </div>
                <div className="text-xs text-fg-mute">
                  Reading device inventory, VLANs, SSIDs, firewall and compliance
                  history. This usually takes 30–50 seconds.
                </div>
              </CardContent>
            </Card>
          ) : shown && site ? (
            <CloseoutCard shown={shown} site={site} />
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-20 text-center">
                <ClipboardCheck size={28} className="text-fg-mute" />
                <div className="text-sm font-semibold text-fg">
                  {site ? "No closeout shown" : "Select a site to begin"}
                </div>
                <div className="max-w-[360px] text-xs text-fg-mute">
                  {site
                    ? "Generate a fresh closeout, or pick a past one from the history rail."
                    : "Choose a site above, then generate the end-of-project closeout document."}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History rail */}
        <div className="min-w-0">
          <Card>
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <span className="text-sm font-bold tracking-tight text-fg">History</span>
              {site ? (
                <span className="font-mono text-[10px] text-fg-mute">{history.length}</span>
              ) : null}
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {!site ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">
                  No site selected.
                </div>
              ) : loadingHistory[slug] && history.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">Loading…</div>
              ) : history.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-fg-mute">
                  No past closeouts for this site.
                </div>
              ) : (
                history.map((h) => {
                  const meta = READINESS_META[h.signoffReadiness];
                  const active = shown?.id === h.id;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => void onPickHistory(h.id)}
                      className={cn(
                        "flex w-full gap-2.5 border-b border-line/60 px-4 py-3 text-left last:border-b-0 hover:bg-surface-2/50",
                        active && "bg-aurora-soft/60",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn("mt-1 inline-block h-2 w-2 shrink-0 rounded-full", meta.dot)}
                      />
                      <div className="min-w-0 flex-1">
                        <Badge variant={meta.badge} size="sm">
                          {h.signoffReadiness}
                        </Badge>
                        <p className="m-0 mt-1 line-clamp-2 text-[11.5px] leading-snug text-fg-dim">
                          {h.summary}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
                          <Cpu size={10} />
                          <span>{timeAgo(h.createdAt)}</span>
                          <span className="opacity-40">·</span>
                          <span className="truncate">{h.createdBy}</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
